import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  RadioGroup,
  Stack,
  Box,
  useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { updateDuplicate } from '@/store/apply/actions';
import { RadioOptionWithLabel } from '@/components/pages/modals/potentialDuplicatesModal/RadioOptionWithLabel';
import { labelFromStudent, subLabelFromStudent } from './helpers';
import { ApplicationState } from '@/store';
import { TooltipAddress } from '@/components/pages/modals/potentialDuplicatesModal/TooltipAddress';
import { GenericsIntegration, PotentialMatchStudent } from 'types';

interface PotentialDuplicatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  duplicateStudents: PotentialMatchStudent[];
  integration: GenericsIntegration.Integrations | '';
}

const NOT_A_DUPLICATE_OPTION_VALUE = 'NOT_A_DUPLICATE';

const PotentialDuplicatesModal: React.FC<PotentialDuplicatesModalProps> = ({
  isOpen,
  onClose,
  duplicateStudents,
  integration,
}) => {
  const dispatch = useDispatch();

  const params = useParams();
  const isLoading = useSelector((state: ApplicationState) => state.ui.isLoading);
  const { response, error } = useSelector((state: ApplicationState) => {
    const { updateDuplicateResponse, updateDuplicateError } = state.apply;
    return { response: updateDuplicateResponse, error: updateDuplicateError };
  });

  const { userId, applicationId, uni } = params;
  const [selectedStudentNo, setSelectedStudentNo] = useState<string>('');
  const toast = useToast();

  const wasStudentNoUpdated = useMemo(() => selectedStudentNo !== NOT_A_DUPLICATE_OPTION_VALUE, [selectedStudentNo]);

  const handleClose = (): void => {
    onClose();
    setSelectedStudentNo(undefined);
  };

  useEffect(() => {
    if (wasStudentNoUpdated) {
      if (response) {
        handleClose();
        toast({
          title: 'Success',
          description: 'Student number assigned successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      if (error) {
        toast({
          title: 'Error',
          description: 'Failed to assign student number',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      if (response) {
        toast({
          title: 'Success',
          description: 'Marked as not a duplicate',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        handleClose();
      }

      if (error) {
        toast({
          title: 'Error',
          description: 'Failed to mark as non-duplicate',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [response, error]);

  const handleAssignStudentNo = (): void => {
    if (wasStudentNoUpdated) {
      dispatch(updateDuplicate({ uni, userId, applicationId, integration, isDuplicate: true, selectedStudentNo }));
    } else {
      dispatch(updateDuplicate({ uni, userId, applicationId, integration, isDuplicate: false }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Potential Duplicate Students Found</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb={4}>
            This applicant may match one or more existing students in your student management system. Please select the
            matching student or indicate that this is not a duplicate.
          </Text>
          {duplicateStudents?.length > 0 && (
            <RadioGroup onChange={setSelectedStudentNo} value={selectedStudentNo}>
              <Stack direction="column" spacing={4}>
                {duplicateStudents?.map(student => (
                  <RadioOptionWithLabel
                    key={student.studentNo}
                    value={student.studentNo}
                    label={labelFromStudent(student)}
                    subLabel={subLabelFromStudent(student)}
                    info={<TooltipAddress address={student.address} />}
                  />
                ))}
                <RadioOptionWithLabel value={NOT_A_DUPLICATE_OPTION_VALUE} label="These are not duplicates" />
              </Stack>
            </RadioGroup>
          )}
          {duplicateStudents?.length === 0 && (
            <Box p={4} borderWidth="1px" borderRadius="md" bg="yellow.50">
              <Text>No potential matches found. This may be a system error.</Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" mr={3} onClick={handleClose} isLoading={isLoading}>
            Cancel
          </Button>
          <Button variant="solid" onClick={handleAssignStudentNo} isDisabled={!selectedStudentNo} isLoading={isLoading}>
            Assign Selected Student
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PotentialDuplicatesModal;
